/**
 * Leaderboards by level (exp), rating (rating + achievement bonus) and assaults (captures), replacing the
 * server snapshot tops of the original (`net_get_tops`, FWP.as:15060-15154; 10 pages x 10 entries).
 * Kept as an in-memory index, rebuilt lazily from storage and updated on every profile save. The entries' clan comes
 * from the clan directory when a page is read (membership changes need no refresh here).
 */
import type { LeaderboardBoard, LeaderboardEntry, LeaderboardPage, PlayerId, ProfileSummary } from '../../../shared/protocol/model';
import { badCursor, cursorInt, decodeCursor, encodeCursor } from '../pagination';
import type { AuthService } from './auth';
import type { ClanDirectory } from './clan-directory';
import type { AccountRecord, ProfileRecord, ServiceDeps } from './deps';
import type { ProfileService } from './profile';

interface IndexEntry {
  playerId: PlayerId;
  displayName: string;
  summary: ProfileSummary;
  /** Per board: when the current value was first reached (ties: earlier first). */
  reachedAt: Record<LeaderboardBoard, number>;
  revision: number;
  banned: boolean;
}

const VALUE: Record<LeaderboardBoard, (s: ProfileSummary) => number> = {
  level: (s) => s.exp,
  rating: (s) => s.rating,
  assaults: (s) => s.assaults,
};

export class LeaderboardService {
  private index = new Map<PlayerId, IndexEntry>();
  private loaded: Promise<void> | null = null;
  private sorted = new Map<LeaderboardBoard, IndexEntry[]>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly profiles: ProfileService,
    private readonly clans: ClanDirectory,
  ) {
    profiles.onSaved((rec, account) => {
      if (this.loaded) this.upsert(rec, account);
    });
  }

  private ensureLoaded(): Promise<void> {
    if (!this.loaded) {
      this.loaded = (async () => {
        const accounts = new Map((await this.auth.listAccounts()).map((a) => [a.playerId, a]));
        for (const rec of await this.profiles.listRecords()) {
          const a = accounts.get(rec.profile.id);
          if (a) this.upsert(rec, a);
        }
      })();
    }
    return this.loaded;
  }

  private upsert(rec: ProfileRecord, a: AccountRecord): void {
    if (rec.profile.revision === 0) return;
    const prev = this.index.get(a.playerId);
    if (prev && prev.revision > rec.profile.revision) return;
    const t = rec.profile.updatedAt;
    const reachedAt = {} as Record<LeaderboardBoard, number>;
    for (const b of Object.keys(VALUE) as LeaderboardBoard[]) {
      reachedAt[b] = prev && VALUE[b](prev.summary) === VALUE[b](rec.profile.summary) ? prev.reachedAt[b] : t;
    }
    this.index.set(a.playerId, {
      playerId: a.playerId,
      displayName: a.displayName,
      summary: rec.profile.summary,
      reachedAt,
      revision: rec.profile.revision,
      banned: this.auth.activeBan(a) !== null,
    });
    this.sorted.clear();
  }

  /** Refreshes account-derived fields (name, ban) of an indexed player. */
  async refreshAccount(playerId: PlayerId): Promise<void> {
    if (!this.loaded) return;
    await this.loaded;
    const e = this.index.get(playerId);
    const a = await this.auth.getAccount(playerId);
    if (!e || !a) return;
    e.displayName = a.displayName;
    e.banned = this.auth.activeBan(a) !== null;
    this.sorted.clear();
  }

  /**
   * An operator restored the player's damaged profile or account from a backup (BackendCore.documentRepaired): the
   * index, loaded without it (the damaged document was skipped), takes the player in at once, so it is back on the
   * boards and matchmaking sees its level without waiting for its next save or a restart. Nothing to do before the
   * index is loaded (the load reads it) or while either document is still missing.
   */
  async repaired(playerId: PlayerId): Promise<void> {
    if (!this.loaded) return;
    await this.loaded;
    const [a, rec] = await Promise.all([this.auth.getAccount(playerId), this.profiles.getRecord(playerId)]);
    if (!a || !rec || rec.profile?.id !== playerId) return;
    this.upsert(rec, a);
  }

  /**
   * Account deletion: the player leaves every board. A load of the index that is under way is awaited first (it may
   * have read the player's profile before the deletion).
   */
  async remove(playerId: PlayerId): Promise<void> {
    if (this.loaded) await this.loaded.catch(() => undefined);
    if (this.index.delete(playerId)) this.sorted.clear();
  }

  private ranking(board: LeaderboardBoard): IndexEntry[] {
    let list = this.sorted.get(board);
    if (!list) {
      const value = VALUE[board];
      list = [...this.index.values()]
        .filter((e) => !e.banned)
        .sort((a, b) => value(b.summary) - value(a.summary) || a.reachedAt[board] - b.reachedAt[board] || (a.playerId < b.playerId ? -1 : 1));
      this.sorted.set(board, list);
    }
    return list;
  }

  private entry(e: IndexEntry, rank: number): LeaderboardEntry {
    return {
      rank,
      playerId: e.playerId,
      displayName: e.displayName,
      level: e.summary.level,
      exp: e.summary.exp,
      rating: e.summary.rating,
      assaults: e.summary.assaults,
      spec: e.summary.spec ?? 0,
      clanId: this.clans.clanIdOf(e.playerId),
      clan: this.clans.briefOf(e.playerId),
    };
  }

  /**
   * A page of a board: by page number (`page`, 1-based, clamped) or by cursor (`opts.cursor`, the `next` of the
   * previous page: `lb.<board>.<offset>`, a position in the ranking). `opts.limit` is the page size of the cursor
   * form (same as `pageSize`). The visible ranking of the cursor form is config.leaderboard.pageSize x maxPages entries
   * whatever the `limit` (the original's 10 x 10 tops); the page-number form shows `pageSize` x maxPages.
   */
  async get(
    board: LeaderboardBoard,
    page = 1,
    pageSize: number | undefined,
    me: PlayerId | null,
    opts: { limit?: number; cursor?: string } = {},
  ): Promise<LeaderboardPage> {
    await this.ensureLoaded();
    await this.clans.ready();
    const cfg = this.d.config.leaderboard;
    const size = opts.limit ?? pageSize ?? cfg.pageSize;
    const list = this.ranking(board);
    const cursorForm = opts.limit !== undefined || opts.cursor !== undefined;
    const visible = Math.min(list.length, (cursorForm ? cfg.pageSize : size) * cfg.maxPages);
    const totalPages = Math.max(1, Math.ceil(visible / size));
    let start: number;
    if (opts.cursor !== undefined) {
      const [b, offset] = decodeCursor(opts.cursor, 'lb', 2);
      if (b !== board) throw badCursor('leaderboard');
      start = Math.min(cursorInt(offset), visible);
    } else {
      start = (Math.min(Math.max(1, page), totalPages) - 1) * size;
    }
    const p = Math.min(Math.floor(start / size) + 1, totalPages);
    const end = Math.min(start + size, visible);
    const entries = list.slice(start, end).map((e, i) => this.entry(e, start + i + 1));
    let mine: LeaderboardEntry | null = null;
    if (me) {
      const idx = list.findIndex((e) => e.playerId === me);
      if (idx >= 0) mine = this.entry(list[idx], idx + 1);
    }
    const next = end < visible ? encodeCursor('lb', [board, end]) : null;
    return { board, page: p, pageSize: size, totalPages, totalEntries: visible, entries, me: mine, items: entries, next };
  }

  /** Current summary of a player from the index (e.g. level for matchmaking). */
  async summaryOf(playerId: PlayerId): Promise<ProfileSummary | null> {
    await this.ensureLoaded();
    return this.index.get(playerId)?.summary ?? null;
  }
}
